import React, { useCallback, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { ScrollingTableHeader, ScrollingTable, ScrollingTableFooter } from '@nextroll/ar-scrolling-table';
import { Link } from '@nextroll/ar-container-app';

import { getContactListDetailsUrl } from 'util/urls';
import { DirType } from 'types/scrollingTable';
import { WorkflowBaseType, WORKFLOW_BASE } from 'types/rofl';

export interface ISettingsPreviewTableCols {
    crm_id: string; // unique ID of contacts
    name?: string;
    email?: string;
    domain?: string;
}
type SettingsPreviewTableColType = keyof ISettingsPreviewTableCols;

/**
 * utility method for sorting in the preview table
 */
const sortHandler = (key: SettingsPreviewTableColType) => (a: ISettingsPreviewTableCols, b: ISettingsPreviewTableCols) => (a[key] || '').localeCompare(b[key] || '');

/**
 * utility method for searching in the preview table
 */
const filterHandler = (key: string = '') => (a: ISettingsPreviewTableCols) => !key || [a.name, a.email, a.domain]
    .filter(Boolean)
    .join('|')
    .toLowerCase()
    .includes(key.toLowerCase());

const getColumns = (base: WorkflowBaseType) => [
    base === WORKFLOW_BASE.CONTACTS && {
        key: 'name' as SettingsPreviewTableColType,
        label: i18n.gettext('Name'),
        render: (col: ISettingsPreviewTableCols) => col.name,
        textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
    },
    base === WORKFLOW_BASE.CONTACTS && {
        key: 'email' as SettingsPreviewTableColType,
        label: i18n.gettext('Email'),
        render: (col: ISettingsPreviewTableCols) => col.email,
        textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
    },
    {
        key: 'domain' as SettingsPreviewTableColType,
        label: i18n.gettext('Domain'),
        render: (col: ISettingsPreviewTableCols) => col.domain,
        textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
    },
].filter(Boolean);

/**
 * preview table pagination size
 */
const PAGE_SIZE = 500;

interface IProps {
    base: WorkflowBaseType;
    data: ISettingsPreviewTableCols[];
    total?: number;
    loading: boolean;
    advertisableEid?: string;
    contactListEid?: string; // for pre-configured contact lists
}

export const SettingsPreviewTable = (props: IProps) => {
    const { base, data, total, loading, advertisableEid, contactListEid } = props;

    const [page, setPage] = useState(1);

    // additional state variables for table sorting and searching
    const [sortBy, setSortBy] = useState({
        key: 'name' as SettingsPreviewTableColType,
        direction: ScrollingTable.SORT_DIRECTION_ASC,
    });
    const [search, setSearch] = useState('');

    const handleSearch = useCallback((e: { target: { value: React.SetStateAction<string> } }) => {
        setSearch(e.target.value);
        if (!!e.target.value && page !== 1) {
            setPage(1); // also go to the first page when search starts
        }
    }, [setSearch, page, setPage]);

    /**
     * data after sorting
     */
    const sortedData = useMemo(() => {
        const sorted = [...data]
            .filter(filterHandler(search))
            .sort(sortHandler(sortBy.key as SettingsPreviewTableColType));
        return sortBy.direction === ScrollingTable.SORT_DIRECTION_ASC ? sorted : sorted.reverse();
    }, [data, search, sortBy]);

    /**
     * paginated data for rendering in the table component
     */
    const rows = useMemo(
        () => sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [sortedData, page],
    );

    /**
     * different header titles depending on account-based or contact-based
     */
    const headerText = useMemo(() => ({
        title: base === WORKFLOW_BASE.CONTACTS
            ? i18n.gettext('Preview {{ count }} contacts', { count: i18n.numeral(sortedData.length).format('0,0') })
            : i18n.gettext('Preview {{ count }} accounts', { count: i18n.numeral(sortedData.length).format('0,0') }),
        subtitle: base === WORKFLOW_BASE.CONTACTS
            ? i18n.gettext('{{ count }} contacts in total', { count: i18n.numeral(total).format('0,0') })
            : i18n.gettext('{{ count }} accounts in total', { count: i18n.numeral(total).format('0,0') }),
    }), [base, sortedData.length, total]);

    return (
        <div className='ar-card'>
            <ScrollingTableHeader>
                <div className='workflows-canvas-setting-panel-preview-search'>
                    <i className='ar-icon ar-icon-search' />
                    <input
                        type='text'
                        value={search}
                        className='form-control'
                        placeholder={i18n.gettext('Search')}
                        onChange={handleSearch}
                    />
                </div>
                <div style={{ flex: 1 }} />
                <div className='workflows-canvas-setting-panel-preview-title'>
                    <h4>{headerText.title}</h4>
                    {total && total > data.length && <small>{headerText.subtitle}</small>}
                </div>
            </ScrollingTableHeader>
            <ScrollingTable
                columns={getColumns(base)}
                rowKey={base === WORKFLOW_BASE.CONTACTS ? 'crm_id' : 'domain'}
                data={rows}
                i18n={i18n}
                stickyHeader
                totalCount={sortedData.length}
                sort={sortBy}
                onSort={(col: SettingsPreviewTableColType, direction: DirType) => setSortBy({ key: col, direction })}
                isLoading={loading}
                pageSize={PAGE_SIZE}
            />
            {sortedData.length > PAGE_SIZE && (
                <ScrollingTableFooter
                    i18n={i18n}
                    pageNumber={page}
                    pageSize={PAGE_SIZE}
                    onGoToPage={setPage}
                    totalCount={sortedData.length}
                    showPageSizeSelector={false}
                >
                    {/* redirect link only appears if this is a pre-configured contact list*/}
                    {base === WORKFLOW_BASE.CONTACTS && contactListEid && (
                        <Link
                            to={getContactListDetailsUrl(contactListEid, advertisableEid)}
                            openNewWindow
                            rel='noopener noreferrer'
                        >
                            <button className='btn btn-default' type='button'>{i18n.gettext('View Full Contact List')}</button>
                        </Link>
                    )}
                </ScrollingTableFooter>
            )}
        </div>
    );
};
